import type { FC } from 'react';

import { Alert } from '@interstate/components/Alert';
import { CheckBox } from '@interstate/components/CheckBox';
import type { CheckBoxEventValue } from '@interstate/components/CheckBox';
import type { InterstateOnChangeCallback } from '@interstate/components/InterstateEvents';
import { Typography } from '@interstate/components/Typography';

import { PRIOR_BALANCE_ENABLE_LABEL, PRIOR_BALANCE_INFO_TITLE } from './constants';
import { PriorBalanceApplyMethod } from './PriorBalanceApplyMethod';
import { PriorBalanceFields } from './PriorBalanceFields';
import { CardContainer, GuidanceBlock } from './priorBalance.styles';
import type { UsePriorBalanceFormResult } from './usePriorBalanceForm';

export type PriorBalanceCardProps = {
    form: UsePriorBalanceFormResult;
};

export const PriorBalanceCard: FC<PriorBalanceCardProps> = ({ form }) => {
    const { formData, applyMethodOptions, lenderOptions, updateField, toggleEnabled } = form;

    const handleEnabledChange: InterstateOnChangeCallback<CheckBoxEventValue> = (event) => {
        toggleEnabled(Boolean(event.target.value.checked));
    };

    return (
        <CardContainer data-testid="prior-balance-card">
            <GuidanceBlock>
                <Alert type="info" role="note" data-testid="prior-balance-info-alert">
                    <Typography variant="strong-sm" tag="span" data-testid="prior-balance-info-title">
                        {PRIOR_BALANCE_INFO_TITLE}
                    </Typography>
                    <Typography variant="body-sm" tag="p" sx={{ margin: 0 }}>
                        <strong>Early termination:</strong> Customer ends the lease early by paying off the remaining scheduled
                        payments. <strong>Negative equity absorption</strong>: If the remaining payments are less than the negative
                        equity (payoff minus trade-in value), it&apos;s cheaper to absorb them than roll the deficiency into the new
                        deal.
                    </Typography>
                </Alert>

                <CheckBox
                    name="priorBalanceEnabled"
                    label={PRIOR_BALANCE_ENABLE_LABEL}
                    data-testid="prior-balance-enabled-checkbox"
                    value="priorBalanceEnabled"
                    checked={formData.isEnabled}
                    onChange={handleEnabledChange}
                />
            </GuidanceBlock>

            {formData.isEnabled && (
                <>
                    <PriorBalanceFields formData={formData} lenderOptions={lenderOptions} onFieldChange={updateField} />
                    <PriorBalanceApplyMethod
                        value={formData.applyMethod}
                        options={applyMethodOptions}
                        onChange={(value) => updateField('applyMethod', value)}
                    />
                </>
            )}
        </CardContainer>
    );
};
